import React, { useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BusinessNumberCell = ({ biz, token, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const number = biz.ai_phone_number;

  const retry = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/businesses/${biz.id}/provision-numbers`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Retry failed');
      if (d.ai_phone_number) {
        toast.success(`Number assigned: ${d.ai_phone_number}`);
        onUpdated(biz.id, { ai_phone_number: d.ai_phone_number });
      } else {
        toast.error(d.retell_purchase_error || 'Still no number available');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const activateSms = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/businesses/${biz.id}/activate-sms`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success('Texting marked live');
      onUpdated(biz.id, { sms_feature_enabled: true, sms_phone_number: d.sms_phone_number });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const retrySms = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/businesses/${biz.id}/retry-sms-setup`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      if (d.sms_status === 'pending_verification') toast.success(`Submitted for carrier approval (${d.sms_phone_number})`);
      else toast.error(d.sms_setup_error || 'Setup still failing');
      onUpdated(biz.id, d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (number) return (
    <div className="space-y-1">
      <span className="text-slate-700 text-sm block" data-testid={`biz-number-${biz.id}`}>{number}</span>
      {biz.sms_addon_paid && ['setup_failed', 'rejected', 'setting_up'].includes(biz.sms_status) && (
        <button type="button" onClick={retrySms} disabled={busy} className="px-2 py-0.5 mr-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors" data-testid={`retry-sms-${biz.id}`}>
          {busy ? 'Working...' : 'Retry Texting'}
        </button>
      )}
      {biz.sms_addon_paid && biz.sms_status === 'pending_verification' && <span className="text-xs text-amber-700 block">Texting: awaiting carrier</span>}
      {biz.sms_addon_paid && !biz.sms_feature_enabled && biz.sms_phone_number && (
        <button type="button" onClick={activateSms} disabled={busy} className="px-2 py-0.5 rounded-md text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 transition-colors" data-testid={`activate-sms-${biz.id}`}>
          {busy ? 'Saving...' : 'Activate Texting'}
        </button>
      )}
      {biz.sms_feature_enabled && <span className="text-xs text-green-700">Texting live</span>}
    </div>
  );
  if (!biz.payment_confirmed) return <span className="text-slate-400 text-sm">Not paid</span>;
  return (
    <div className="flex items-center gap-2">
      <span className="px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-200">Missing</span>
      <button
        type="button"
        onClick={retry}
        disabled={busy}
        className="px-2 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-60"
        data-testid={`retry-number-${biz.id}`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Retry Number
      </button>
    </div>
  );
};
